import { RefObject, useCallback, useEffect, useState } from 'react';
import { SystemStyleObject } from '@chakra-ui/react';

/**
 * WebKit has never shipped the unprefixed Fullscreen API surface, so every entry point below
 * has to check both spellings — an unprefixed-only call is a silent no-op in Safari.
 */
type FullscreenDocument = Document & {
    webkitFullscreenElement?: Element | null;
    webkitFullscreenEnabled?: boolean;
    webkitExitFullscreen?: () => void;
};

type FullscreenTarget = HTMLElement & {
    webkitRequestFullscreen?: () => void;
};

const getFullscreenElement = (): Element | null => {
    const doc = document as FullscreenDocument;

    return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
};

/**
 * iPhone has no element-fullscreen API at all — per MDN's compat data, both the standard and
 * the `webkit`-prefixed `requestFullscreen` are "Only available on iPad, not on iPhone", and
 * Chrome on iOS is WebKit underneath, so it inherits the same gap. `fullscreenEnabled` also
 * reports false inside an iframe that wasn't granted the fullscreen permission. Either way
 * there is nothing to call, so we fall back to sizing the element ourselves.
 */
const canUseNativeFullscreen = (element: FullscreenTarget): boolean => {
    const doc = document as FullscreenDocument;
    const enabled = doc.fullscreenEnabled ?? doc.webkitFullscreenEnabled ?? false;

    return enabled && Boolean(element.requestFullscreen ?? element.webkitRequestFullscreen);
};

const PSEUDO_FULLSCREEN_ATTRIBUTE = 'data-fullscreen';
const PSEUDO_FULLSCREEN_VALUE = 'pseudo';

/**
 * The fallback "fullscreen": the element covers the viewport on its own. The browser's own
 * chrome stays visible — there is no way to hide it without the real API — but the player
 * fills the screen, which is what the control promises. Same approach video.js and the major
 * web players take on iPhone.
 *
 * `vh` is the baseline; `dvh` tracks Safari's collapsing toolbars so the canvas doesn't end up
 * underneath them. They go in a `@supports` block rather than two `height` keys, because a
 * style object can only carry one value per property — the usual CSS fallback pair isn't
 * expressible here.
 */
const pseudoFullscreenSizing = {
    position: 'fixed',
    inset: 0,
    width: '100vw',
    height: '100vh',
    '@supports (height: 100dvh)': {
        height: '100dvh'
    },
    // Clears the studio lightbox (backdrop 10000, controls 10002) and the client gallery's
    // detail view (tops out at 100) without reaching for a max-int z-index.
    zIndex: 10100,
    backgroundColor: 'black'
};

export const fullscreenContainerSx: SystemStyleObject = {
    containerType: 'size',
    [`&[${PSEUDO_FULLSCREEN_ATTRIBUTE}='${PSEUDO_FULLSCREEN_VALUE}']`]: pseudoFullscreenSizing
};

/**
 * Tracks and toggles fullscreen for `targetRef`, using the real Fullscreen API where it exists
 * and falling back to viewport-filling CSS where it doesn't (see `canUseNativeFullscreen`).
 *
 * Pair it with `fullscreenContainerSx` on the same element and spread `containerProps` onto it
 * — the sx object carries the fallback styling and `containerProps` is what switches it on.
 */
const useFullscreen = <T extends HTMLElement>(targetRef: RefObject<T>) => {
    const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);
    const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);

    useEffect(() => {
        const onFullscreenChange = () => {
            setIsNativeFullscreen(getFullscreenElement() === targetRef.current);
        };

        document.addEventListener('fullscreenchange', onFullscreenChange);
        document.addEventListener('webkitfullscreenchange', onFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', onFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
        };
    }, [targetRef]);

    // The real API exits on Escape for free; the fallback has to do it by hand or the only way
    // out would be the control itself.
    useEffect(() => {
        if (!isPseudoFullscreen) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsPseudoFullscreen(false);
            }
        };

        document.addEventListener('keydown', onKeyDown);

        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isPseudoFullscreen]);

    const toggleFullscreen = useCallback(() => {
        const element = targetRef.current as FullscreenTarget | null;

        if (!element) {
            return;
        }

        // Leaving native fullscreen is checked before support is, not after: if we are already
        // in it the API plainly exists, and testing support first would strand the element
        // there whenever that check disagreed.
        if (getFullscreenElement() === element) {
            const doc = document as FullscreenDocument;

            if (doc.exitFullscreen) {
                // Rejects rather than throwing, and an unhandled rejection is what made the
                // button look broken with nothing in the console to explain it.
                doc.exitFullscreen().catch(() => undefined);
            } else {
                doc.webkitExitFullscreen?.();
            }

            return;
        }

        if (!canUseNativeFullscreen(element)) {
            setIsPseudoFullscreen((wasFullscreen) => !wasFullscreen);

            return;
        }

        // Deliberately not guarded on "nothing else is fullscreen" — requesting fullscreen for
        // a second element is a valid swap, and guarding it left the button dead whenever
        // something else on the page held fullscreen.
        if (element.requestFullscreen) {
            element.requestFullscreen().catch(() => undefined);
        } else {
            element.webkitRequestFullscreen?.();
        }
    }, [targetRef]);

    return {
        isFullscreen: isNativeFullscreen || isPseudoFullscreen,
        toggleFullscreen,
        containerProps: {
            [PSEUDO_FULLSCREEN_ATTRIBUTE]: isPseudoFullscreen ? PSEUDO_FULLSCREEN_VALUE : undefined
        }
    };
};

export default useFullscreen;
